(() => {
  const initCompare = (root) => {
    root.querySelectorAll('[data-cl-compare]').forEach((frame) => {
      const range = frame.querySelector('input[type="range"]');
      if (!range || range.dataset.bound) return;
      range.dataset.bound = 'true';
      const update = () => {
        frame.style.setProperty('--pos', `${range.value}%`);
        range.setAttribute('aria-valuenow', range.value);
        range.setAttribute('aria-valuetext', `${range.value}% before`);
      };
      range.addEventListener('input', update);
      update();
      if (!document.documentElement.classList.contains('cl-motion') || !('IntersectionObserver' in window)) return;
      const hint = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        hint.disconnect();
        const start = Number(range.value);
        const keys = [start, start - 14, start + 14, start];
        const t0 = performance.now();
        const step = (now) => {
          const p = Math.min((now - t0) / 1800, 1) * (keys.length - 1);
          const k = Math.min(Math.floor(p), keys.length - 2);
          const e = 0.5 - Math.cos((p - k) * Math.PI) / 2;
          frame.style.setProperty('--pos', `${keys[k] + (keys[k + 1] - keys[k]) * e}%`);
          if (p < keys.length - 1 && !frame.dataset.touched) requestAnimationFrame(step);
          else update();
        };
        setTimeout(() => requestAnimationFrame(step), 400);
      }, { threshold: 0.6 });
      hint.observe(frame);
      range.addEventListener('pointerdown', () => (frame.dataset.touched = 'true'));
      range.addEventListener('keydown', () => (frame.dataset.touched = 'true'));
    });
  };

  const initTabs = (root) => {
    root.querySelectorAll('[data-cl-tabs]').forEach((wrap) => {
      if (wrap.dataset.bound) return;
      wrap.dataset.bound = 'true';
      const tabs = [...wrap.querySelectorAll('[role="tab"]')];
      const select = (tab, focus) => {
        tabs.forEach((t) => {
          const active = t === tab;
          t.setAttribute('aria-selected', active);
          t.tabIndex = active ? 0 : -1;
          const panel = wrap.querySelector(`#${t.getAttribute('aria-controls')}`);
          if (panel) panel.hidden = !active;
        });
        if (focus) tab.focus();
      };
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', (e) => {
          const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
          if (e.key in keys) {
            e.preventDefault();
            select(tabs[(i + keys[e.key] + tabs.length) % tabs.length], true);
          } else if (e.key === 'Home' || e.key === 'End') {
            e.preventDefault();
            select(tabs[e.key === 'Home' ? 0 : tabs.length - 1], true);
          }
        });
      });
    });
  };

  const initGallery = (root) => {
    root.querySelectorAll('[data-cl-gallery]').forEach((gallery) => {
      if (gallery.dataset.bound) return;
      gallery.dataset.bound = 'true';
      const main = gallery.querySelector('.cl-main-img');
      const thumbs = [...gallery.querySelectorAll('[data-cl-thumb]')];
      if (!main || !thumbs.length) return;
      let index = 0;
      const show = (i) => {
        index = (i + thumbs.length) % thumbs.length;
        const t = thumbs[index];
        main.src = t.dataset.src;
        main.srcset = t.dataset.srcset;
        main.alt = t.dataset.alt;
        main.classList.remove('is-swapping');
        void main.offsetWidth;
        main.classList.add('is-swapping');
        thumbs.forEach((x) => x.setAttribute('aria-current', x === t));
      };
      thumbs.forEach((t, i) => t.addEventListener('click', () => show(i)));
      let startX = null;
      main.addEventListener('touchstart', (e) => (startX = e.touches[0].clientX), { passive: true });
      main.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) show(index + (dx < 0 ? 1 : -1));
        startX = null;
      });
    });
  };

  const initBuyNow = (root) => {
    root.querySelectorAll('[data-cl-buy-now]').forEach((btn) => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', async () => {
        const form = document.getElementById(btn.dataset.form);
        if (!form) return;
        const data = new FormData(form);
        btn.setAttribute('aria-disabled', 'true');
        try {
          const res = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: data,
          });
          if (!res.ok) throw new Error((await res.json()).description || 'Could not add to cart');
          window.location.href = '/checkout';
        } catch (err) {
          btn.removeAttribute('aria-disabled');
          const msg = btn.parentElement.querySelector('[data-cl-buy-error]');
          if (msg) {
            msg.textContent = err.message;
            msg.hidden = false;
          }
        }
      });
    });
  };

  const initReveal = (root) => {
    const items = [...root.querySelectorAll('[data-cl-reveal]:not(.is-in)')];
    if (!items.length) return;
    if (!document.documentElement.classList.contains('cl-motion') || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    items.forEach((el) => io.observe(el));
  };

  const motion = () => document.documentElement.classList.contains('cl-motion');

  let parallaxItems = [];
  let parallaxQueued = false;
  const updateParallax = () => {
    parallaxQueued = false;
    const vh = window.innerHeight;
    parallaxItems.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.bottom < 0 || r.top > vh) return;
      const progress = (vh - r.top) / (vh + r.height);
      el.style.setProperty('--py', `${((progress - 0.5) * 0.07 * r.height).toFixed(1)}px`);
    });
  };
  const queueParallax = () => {
    if (parallaxQueued) return;
    parallaxQueued = true;
    requestAnimationFrame(updateParallax);
  };
  const initParallax = () => {
    if (!motion()) return;
    const first = !parallaxItems.length;
    parallaxItems = [...document.querySelectorAll('[data-cl-parallax]')];
    if (first) {
      window.addEventListener('scroll', queueParallax, { passive: true });
      window.addEventListener('resize', queueParallax);
    }
    queueParallax();
  };

  const initZoom = (root) => {
    if (!window.matchMedia('(hover: hover) and (min-width: 768px)').matches) return;
    root.querySelectorAll('.cl-main-media').forEach((media) => {
      if (media.dataset.zoomBound) return;
      media.dataset.zoomBound = 'true';
      media.addEventListener('pointermove', (e) => {
        const r = media.getBoundingClientRect();
        media.style.setProperty('--zx', `${((e.clientX - r.left) / r.width) * 100}%`);
        media.style.setProperty('--zy', `${((e.clientY - r.top) / r.height) * 100}%`);
      });
      media.addEventListener('pointerenter', () => media.classList.add('is-zooming'));
      media.addEventListener('pointerleave', () => media.classList.remove('is-zooming'));
    });
  };

  const initSort = (root) => {
    root.querySelectorAll('[data-cl-sort]').forEach((select) => {
      if (select.dataset.bound) return;
      select.dataset.bound = 'true';
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });
    });
  };

  const initStickyBuy = (root) => {
    const bar = root.querySelector('[data-cl-sticky-buy]');
    const block = root.querySelector('[data-cl-buy-block]');
    if (!bar || !block || bar.dataset.bound || !('IntersectionObserver' in window)) return;
    bar.dataset.bound = 'true';
    const mobile = window.matchMedia('(max-width: 767px)');
    let blockVisible = true;
    const update = () => {
      const show = mobile.matches && !blockVisible;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      bar.querySelectorAll('button').forEach((b) => (b.tabIndex = show ? 0 : -1));
      document.body.classList.toggle('cl-has-sticky-buy', show);
    };
    new IntersectionObserver((entries) => {
      blockVisible = entries[0].isIntersecting;
      update();
    }).observe(block);
    mobile.addEventListener('change', update);
  };

  const initHowTo = (root) => {
    const videos = root.querySelectorAll('[data-cl-howto-video]:not([data-bound])');
    if (!videos.length || !('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          const v = e.target;
          if (e.isIntersecting) {
            if (v.preload !== 'auto') v.preload = 'auto';
            const p = v.play();
            if (p && p.catch) p.catch(() => {});
          } else {
            v.pause();
          }
        }),
      { rootMargin: '100px 0px' }
    );
    videos.forEach((v) => {
      v.dataset.bound = 'true';
      v.muted = true;
      io.observe(v);
    });
  };

  let pauseObserver;
  const initPause = (root) => {
    if (!('IntersectionObserver' in window)) return;
    pauseObserver =
      pauseObserver ||
      new IntersectionObserver(
        (entries) => entries.forEach((e) => e.target.classList.toggle('cl-offscreen', !e.isIntersecting)),
        { rootMargin: '200px 0px' }
      );
    root.querySelectorAll('.shopify-section').forEach((s) => pauseObserver.observe(s));
    if (root.classList && root.classList.contains('shopify-section')) pauseObserver.observe(root);
  };

  const refreshCartBubble = () => {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    fetch(`${window.Shopify && Shopify.routes ? Shopify.routes.root : '/'}?sections=cart-icon-bubble`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data || !data['cart-icon-bubble']) return;
        const doc = new DOMParser().parseFromString(data['cart-icon-bubble'], 'text/html');
        const fresh = doc.querySelector('.shopify-section');
        if (fresh) bubble.innerHTML = fresh.innerHTML;
      })
      .catch(() => {});
  };

  window.addEventListener('pageshow', (e) => {
    if (e.persisted) refreshCartBubble();
  });

  const init = (root = document) => {
    initPause(root);
    initReveal(root);
    initParallax();
    initZoom(root);
    initSort(root);
    initCompare(root);
    initTabs(root);
    initGallery(root);
    initBuyNow(root);
    initStickyBuy(root);
    initHowTo(root);
  };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
